import React from "react";

import { isEmpty } from "lodash/fp";

import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import {
  GPT_ENTITY_CONFIG,
  GPT_ENTITY_KEYS,
} from "@app/analytics/Home/components/constants";
import SharedAlert from "@app/analytics/Home/components/SharedAlert";
import type { MeetingReportFeedItem } from "@app/analytics/Home/types";
import { getFormattedMeetingReportDate } from "@app/analytics/Home/utils/parsingUtils";
import useSessionChaptersQuery from "@app/analytics/hooks/meetings/useSessionChaptersQuery";
import useRecordingInfo from "@app/analytics/hooks/recordings/useRecordingInfo";
import ROUTES from "@app/common/routes";
import {
  BaseSessionChapter,
  UnauthorizedVideoRecording,
  VideoRecording,
} from "@app/common/types";
import {
  getRelativeTimestamp,
  pluralizePhrase,
  truncateAlongWordBoundary,
  withQueryParams,
} from "@app/common/utils";

import { Link } from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import { Row, Column } from "@rui/Stack";
import { List } from "@rui/styles";

import DetailsPanelHeader from "./DetailsPanelHeader";
import { DetailsPanelContent, DetailsPanelFooter, Panel } from "./styles";
import { ActionButton, ScoreBox } from "../styles";
import * as FeedComponentUtils from "../utils";

interface MeetingReportDetailsPanelProps {
  item: MeetingReportFeedItem;
  onClose: () => void;
}

const getChapterTimestamps = ({
  chapter,
  recordingInfo,
  session,
}: {
  chapter: BaseSessionChapter;
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording;
  session: MeetingReportFeedItem["session"];
}) => {
  const formattedTimestamp = getRelativeTimestamp(
    session.startTime,
    chapter.startTime
  );

  const rawTimestamp = (
    recordingInfo && recordingInfo.status !== "unauthorized"
      ? chapter.startTime.diff(recordingInfo.startTime)
      : chapter.startTime.diff(session.startTime)
  ).as("seconds");

  return {
    display: formattedTimestamp,
    raw: Math.max(0, rawTimestamp),
  };
};

const MeetingReportDetailsPanel = ({
  item,
  onClose,
}: MeetingReportDetailsPanelProps) => {
  const hasTopLevelMetrics = FeedComponentUtils.itemHasAffectiveMetrics(item);
  const hasGptEntities = FeedComponentUtils.itemHasGptEntities(item);

  const isNotetakerReport = item.data.reportType === "notetaker";

  const { recordingInfo, isLoading: recordingIsLoading } = useRecordingInfo(
    item.session.id,
    { enabled: !isNotetakerReport }
  );

  const { chapters, isLoading: chaptersIsLoading } = useSessionChaptersQuery(
    item.session.id,
    { enabled: !isNotetakerReport }
  );

  const isLoading = recordingIsLoading || chaptersIsLoading;

  if (isLoading) {
    return (
      <Panel>
        <DetailsPanelHeader item={item} onClose={onClose} />
        <DetailsPanelContent variant="center" minHeight="100px">
          <LoadSpinner />
        </DetailsPanelContent>
      </Panel>
    );
  }

  const meetingReportUrl = ROUTES.meetingReportById(item.session.id);
  const hasSummary = Boolean(item.data.metrics.summary);

  return (
    <Panel>
      <DetailsPanelHeader
        item={item}
        onClose={onClose}
        alert={
          item.data.sharing ? (
            <SharedAlert
              {...item.data.sharing}
              sx={{ borderRadius: "4px", paddingY: 0.5 }}
            />
          ) : null
        }
      />
      <DetailsPanelContent spacing={1}>
        {isNotetakerReport && (
          <>
            <Typography variant="bodySecondaryMed">
              {getFormattedMeetingReportDate(item.session.startTime)}
            </Typography>
            <Divider />
          </>
        )}
        {hasTopLevelMetrics && (
          <>
            <Row spacing={1} variant="apart">
              <ScoreBox
                score={item.data.metrics.readScore}
                label="Read Score"
              />
              <ScoreBox
                score={item.data.metrics.engagement}
                label="Engagement"
              />
              <ScoreBox score={item.data.metrics.sentiment} label="Sentiment" />
            </Row>
            <Divider />
          </>
        )}
        {(hasSummary || !isNotetakerReport) && (
          <>
            <Column spacing={0.5}>
              <Typography variant="bodySecondaryMed">Summary</Typography>
              {hasSummary ? (
                <Typography variant="bodySecondaryReg">
                  {truncateAlongWordBoundary(item.data.metrics.summary, 250)}
                </Typography>
              ) : (
                <Typography variant="bodySecondaryReg" fontStyle="italic">
                  No summary generated
                </Typography>
              )}
            </Column>
            {hasSummary && <Divider />}
          </>
        )}
        {!isEmpty(chapters) && (
          <>
            <Column spacing={0.5}>
              <Typography variant="bodySecondaryMed">Chapters</Typography>
              <List>
                {chapters.map((chapter) => {
                  const timestamps = getChapterTimestamps({
                    chapter,
                    recordingInfo,
                    session: item.session,
                  });
                  return (
                    <li key={chapter.id}>
                      <Typography variant="bodySecondaryReg">
                        {chapter.title} (
                        <Link
                          underline
                          fontWeight={400}
                          color="inherit"
                          to={withQueryParams(meetingReportUrl, {
                            t: timestamps.raw,
                          })}
                        >
                          {timestamps.display}
                        </Link>
                        )
                      </Typography>
                    </li>
                  );
                })}
              </List>
            </Column>
            <Divider />
          </>
        )}
        {hasGptEntities && (
          <>
            <Column spacing={0.5}>
              <Typography variant="bodySecondaryMed">Outcomes</Typography>
              {GPT_ENTITY_KEYS.map((metric) => {
                const value = item.data.metrics[metric] as number;
                if (!value) return null;

                const { icon, label } = GPT_ENTITY_CONFIG[metric]!;
                return (
                  <Row variant="left" spacing={1} key={metric}>
                    <icon.Icon sx={{ color: icon.color, fontSize: "16px" }} />
                    <Typography variant="bodySecondaryReg">
                      {pluralizePhrase(value, label)} (
                      <Link
                        underline
                        to={meetingReportUrl}
                        fontWeight={400}
                        color="inherit"
                      >
                        view
                      </Link>
                      )
                    </Typography>
                  </Row>
                );
              })}
            </Column>
          </>
        )}
        {/* TODO (ENG-9797): add highlight reel here if user has access to the video */}
      </DetailsPanelContent>
      <DetailsPanelFooter>
        <Link to={meetingReportUrl}>
          <ActionButton>View report</ActionButton>
        </Link>
      </DetailsPanelFooter>
    </Panel>
  );
};

export default MeetingReportDetailsPanel;
